import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useGetUserProfile } from '@/api/user-dashboard/queries';
import {
  useWalletMfaManagement,
  type WalletMfaManagement,
} from '@/features/wallet-mfa/use-wallet-mfa-management';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';

import { useWalletAccess } from './wallet-access-provider';
import { walletSecurityReminder } from './wallet-security-reminder-store';
import {
  WalletSendSuggestionController,
  type WalletSendSuggestionSnapshot,
} from './wallet-send-suggestion';

export interface WalletSendSuggestion extends WalletSendSuggestionSnapshot {
  enrolling: boolean;
  management: WalletMfaManagement;
  request: (send: () => void) => Promise<void>;
  continueSend: () => void;
  addAuthenticator: () => void;
  closeEnrollment: () => void;
  cancel: () => void;
}

export function useWalletSendSuggestion(active: boolean): WalletSendSuggestion {
  const { beginOperation, isUnlocked } = useWalletAccess();
  const management = useWalletMfaManagement();
  const takeReminder = useSendReminder(management.enabled);
  const controller = useMemo(
    () => new WalletSendSuggestionController({ beginOperation, takeReminder }),
    [beginOperation, takeReminder],
  );
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const enrollment = useSuggestedEnrollment(controller, active && isUnlocked);

  return {
    ...snapshot,
    ...enrollment,
    management,
    request: controller.request,
    continueSend: controller.continueSend,
    cancel: controller.cancel,
  };
}

function useSuggestedEnrollment(controller: WalletSendSuggestionController, active: boolean) {
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    if (!active) {
      controller.cancel();
      setEnrolling(false);
    }

    return controller.cancel;
  }, [active, controller]);

  const addAuthenticator = () => {
    if (controller.addAuthenticator()) {
      setEnrolling(true);
    }
  };

  return { enrolling, addAuthenticator, closeEnrollment: () => setEnrolling(false) };
}

function useSendReminder(hasTotp: boolean): () => Promise<boolean> {
  const { user } = useGetUserProfile();
  const enrollmentEnabled = useFeatureFlag(FeatureFlags.WALLET_MFA_ENROLLMENT) === true;
  const ownerId = user?.id ?? '';
  return useCallback(
    async () => enrollmentEnabled && walletSecurityReminder.take(ownerId, hasTotp),
    [enrollmentEnabled, hasTotp, ownerId],
  );
}
